import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme, FormControl, Select, MenuItem } from "@mui/material";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Building } from "../../../db_access/building";
import { useBuildingInsight } from "../../../utilities/useBuildingQueries";

const BuildingInsight = ({ buildingId }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const [elapsed, setElapsed] = useState(0);
    const [activeModel, setActiveModel] = useState(null); // Track which model generated the current response
    const timerRef = useRef(null);
    const tableHeaders = ["Category", "Hotspot", "Recommendation"];
    const tableRows = ["category", "hotspot", "recommendation"];

    // CACHED: Use TanStack Query hook (30-min staleTime) instead of raw useEffect fetch.
    // This prevents the AI endpoint from being called on every tab switch or refresh.
    const { data: ai_insight, refetch: refetchInsight } = useBuildingInsight(buildingId);

    const startTimer = async () => {
        setElapsed(0);
        const start = Date.now();
        timerRef.current = setInterval(() => {
            setElapsed(Date.now() - start);
        }, 1);
    };

    const endTimer = () => clearInterval(timerRef.current);

    const generateInsight = (generating = () => { }, modelLabel) => {
        if (!buildingId) return;
        startTimer();
        setActiveModel(modelLabel); // Store which model is being used
        generating(buildingId)
            .catch((err) => {
                console.error("Failed to generate insight:", err);

            })
            .finally((_) => {
                // Use refetch() from TanStack Query to refresh cached data after generation.
                refetchInsight();
                endTimer();
            });
    };

    const buttonOptions = [
        { label: "5.0", action: Building.generate_insight_5_0 },
        { label: "3.5", action: Building.generate_insight_3_5 },
        { label: "deepseek", action: Building.generate_insight_deepseek },
        { label: "gemini flash", action: Building.generate_insight_gemini_flash },
        { label: "gemini pro", action: Building.generate_insight_gemini_pro },
        { label: "gemini flash lite", action: Building.generate_insight_gemini_flash_lite },
    ];

    return (
        <Box>
            {/* Buttons row */}
            {/* Select row */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, paddingX: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ color: "textSecondary", fontWeight: 500, fontSize: "15px" }}>
                    Powered by
                </Typography>
                <FormControl variant="outlined" size="small">
                    <Select
                        value={activeModel || "5.0"}
                        onChange={(e) => {
                            const val = e.target.value;
                            const model = buttonOptions.find(b => b.label === val);
                            if (model) {
                                generateInsight(model.action, model.label);
                            }
                        }}
                        displayEmpty
                        sx={{
                            height: 38,
                            minWidth: 160,
                            fontSize: "14px",
                            borderRadius: "8px",
                            fontWeight: 600,
                            bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: isDark ? "#3f3f46" : "#e2e8f0",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "primary.main",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "primary.main",
                            }
                        }}
                    >
                        {buttonOptions.map((b, index) => (
                            <MenuItem key={b.label} value={b.label} sx={{ fontSize: "14px", fontWeight: 500 }}>
                                {b.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* elapsed */}
            <Box sx={{ marginTop: 2, paddingX: 2 }}>
                <Typography sx={{ color: isDark ? "#f1f5f9" : "inherit" }}>
                    {t("Time")}: {elapsed} ms{" "}
                </Typography>
            </Box>

            {/* AI insights text */}
            <Box sx={{ padding: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {ai_insight?.insights &&
                        ai_insight.insights.map((i, index) => (
                            <Box key={index}>
                                <Typography
                                    sx={{
                                        fontFamily: "Poppins",
                                        textAlign: "left",
                                        fontSize: 16,
                                        color: isDark ? "#e4e4e7" : "#334155",
                                        fontWeight: 400,
                                    }}
                                >
                                    {i}
                                </Typography>
                            </Box>
                        ))}
                </Box>

                <Box sx={{ paddingY: 2 }} />

                {/* Table */}
                {ai_insight?.env_hotspot && (
                    <Box>
                        <TableContainer component={Paper} elevation={0} sx={{ border: isDark ? '1px solid #3f3f46' : '1px solid #e2e8f0', backgroundColor: isDark ? '#1c1c1e' : 'white' }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ background: isDark ? '#27272a' : '#f8fafc' }}>
                                        {tableHeaders.map((h, i) => (
                                            <TableCell key={i} sx={{ fontFamily: "Poppins", fontSize: 14, fontWeight: 600, color: isDark ? "#94a3b8" : "#64748b" }}>
                                                {t(h)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {ai_insight.env_hotspot.map((e, index) => (
                                        <TableRow key={index} sx={{ '&:hover': { backgroundColor: isDark ? '#27272a' : '#f8fafc' } }}>
                                            {tableRows.map((row, i) => (
                                                <TableCell
                                                    key={i}
                                                    sx={{ fontFamily: "Poppins", fontSize: 14, color: isDark ? "#e4e4e7" : "#334155" }}
                                                >
                                                    {e[row]}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
export default BuildingInsight;

